import { FilterField, FilterFieldType, FilterFieldConfig } from '../filter';
import { SelectorConfig, ItemAndStatus, ItemAndStatusOptions } from '../../../va-items-and-status/va-items-and-status';

export interface ItemsAndStatusFilterFieldConfig extends FilterFieldConfig {
  options?: ItemAndStatusOptions[];
  itemSelectorConfig?: SelectorConfig;
  statusSelectorConfig?: SelectorConfig;
  editionSelectorConfig?: SelectorConfig;
}

export class MultiItemsAndStatusFilterField extends FilterField<ItemAndStatus[]> {
  readonly type: FilterFieldType = 'multi-items-and-status';
  options: ItemAndStatusOptions[];
  itemSelectorConfig: SelectorConfig | null;
  statusSelectorConfig: SelectorConfig | null;
  editionSelectorConfig: SelectorConfig | null;

  constructor(config: ItemsAndStatusFilterFieldConfig) {
    super(config);
    this.options = config.options || [];
    this.itemSelectorConfig = config.itemSelectorConfig || null;
    this.statusSelectorConfig = config.statusSelectorConfig || null;
    this.editionSelectorConfig = config.editionSelectorConfig || null;
    this.value = config.value || null;
  }

  updateValue(oldValue: ItemAndStatus, newValue: ItemAndStatus): void {
    const indexOfExisting = this.value!.findIndex((existing) => existing.item === oldValue.item);
    if (indexOfExisting >= 0) {
      this.value![indexOfExisting] = newValue;
    }
    if (this.valueChanged) {
      this.valueChanged(this);
    }
  }

  removeValue(value: ItemAndStatus): void {
    if (Array.isArray(this.value)) {
      const index = this.value.indexOf(value);
      this.value.splice(index, 1);
    }
    if (this.valueChanged) {
      this.valueChanged(this);
    }
  }

  public clearValue(): void {
    this.value = [];
    if (this.valueChanged) {
      this.valueChanged(this);
    }
  }

  appliedFieldText(value: ItemAndStatus): string {
    // Destructure display properties for cleaner access
    const itemDisplayProperty = this.itemSelectorConfig!.displayProperty!;
    const statusDisplayProperty = this.statusSelectorConfig!.displayProperty!;
    const editionDisplayProperty = this.editionSelectorConfig!.displayProperty!;

    // Construct the base string with item
    let resultString = `${this.name}: ${value.item[itemDisplayProperty]}`;

    // Conditionally append edition if it's not null
    if (value.edition && editionDisplayProperty) {
      resultString += ` ${value.edition[editionDisplayProperty]}`;
    }

    resultString += `: ${value.status[statusDisplayProperty]}`;

    return resultString;
  }

  serializeValue(): string {
    if (!Array.isArray(this.value) || this.value.length === 0) {
      return '';
    }

    const itemDisplayProperty = this.itemSelectorConfig!.displayProperty!;
    const statusDisplayProperty = this.statusSelectorConfig!.displayProperty!;
    const editionDisplayProperty = this.editionSelectorConfig!.displayProperty!;

    // Use map to transform each value to a string and join with commas
    const queryParts = this.value.map((value) => {
      // Base string with item and status
      let part = `${value.item[itemDisplayProperty]}:${value.status[statusDisplayProperty]}`;

      // Conditionally append edition if it's not null
      if (value.edition && editionDisplayProperty) {
        part += `:${value.edition[editionDisplayProperty]}`;
      }

      return part;
    });

    // Join all parts with a comma and return
    return queryParts.join(',');
  }

  deserializeValue(urlParam: string): ItemAndStatus[] | null {
    const valueList = urlParam.split(',');
    const matches: ItemAndStatus[] = [];
    valueList.forEach((value) => {
      const paramItem = value.split(':')[0];
      const paramStatus = value.split(':')[1];
      const paramEdition = value.split(':')[2];
      // find the matching Item
      const matchingItem = this.options.find(
        (option) => option.item[this.itemSelectorConfig!.displayProperty!] === paramItem,
      );
      if (matchingItem) {
        // find the matching status in the item
        const matchingStatus = matchingItem.statuses.find(
          (status) => status[this.statusSelectorConfig!.displayProperty!] === paramStatus,
        );
        const matchingEdition = matchingItem.editions?.find((edition) => edition.editionId === paramEdition);
        if (matchingStatus) {
          // append the matching ItemAndStatus to list that we will return
          matches.push({ item: matchingItem.item, status: matchingStatus, edition: matchingEdition });
        }
      }
    });
    // If there are no matching items in the list, return null
    return matches.length > 0 ? matches : null;
  }
}
